import React, { Component, lazy } from 'react'
import { withRouter } from 'react-router'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Helmet } from 'react-helmet'
import get from 'lodash.get'
import { toast } from 'react-toastify'
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons'

import { decodeEntities, translate } from '../../helpers/stringHelpers'
import { retry } from '../../helpers/chunkHelpers'
import withWebSockets from '../withWebSockets'

import { auctions, notifications } from '../../actions'
import ConfirmModal from '../../components/confirmModal'
import Loading from '../../components/loading'
import ErrorNotFound from '../errorNotFound/errorNotFound'
import ConfigContext from '../../contexts/configContext'

const TimedDetails = lazy(() => retry(() => import('./timedDetails')))
const WebcastDetails = lazy(() => retry(() => import('./webcastDetails')))

const marketplacePath = '/marketplace'

export class AuctionDetails extends Component {
  static contextType = ConfigContext
  _registrationStatus = null

  _isMarketplace = () => this.props.match.path.indexOf(marketplacePath) === 0

  constructor () {
    super()
    this.state = {
      hasStarted: false,
      notFound: false
    }
    this.fetchData = this.fetchData.bind(this)
  }

  async componentDidMount () {
    if (this.props.isInteractive) {
      await this.fetchData()
    }
    const userUuid = this.props.userUuid
    const isMarketplace = this._isMarketplace()
    const auctionStatus = this.props.auctions[this.props.match.params.auctionUuid]?.status
    const auctionHasEnded = auctionStatus === 'RECONCILIATION' || auctionStatus === 'ARCHIVED'
    if (!isMarketplace) {
      const registration = userUuid && get(this.props.registrantsByUserUuid, `${this.props.match.params.auctionUuid}.${userUuid}`)
      const registrationStatusToastId = `registrationToast${this.props.match.params.auctionUuid}`
      if (registration && !registration.approved) {
        this._registrationStatus = toast.warn(registration.statusLabel, {
          toastId: registrationStatusToastId,
          autoClose: false
        })
      }
      if (userUuid && !registration && !auctionHasEnded) {
        this._registrationStatus = toast.info(translate('JspPublicCodes_JSP_WARNING_UNAUTHENTICATED_BID'), {
          toastId: registrationStatusToastId,
          autoClose: false
        })
      }
    }
    const marketplaceFriendlyAuctionUuid = isMarketplace ? 'marketplace' : this.props.match.params.auctionUuid
    const auction = this.props.auctions[marketplaceFriendlyAuctionUuid]
    if (auction) {
      this.checkStartTime(auction.startsAt)
    }
  }

  componentDidUpdate (prevProps) {
    if ((this.props.match.params.auctionUuid !== prevProps.match.params.auctionUuid ||
         prevProps.isInteractive !== this.props.isInteractive) &&
         this.props.isInteractive) {
      this.fetchData()
    }
    const languageChanged =
      prevProps.currentLanguageCode != null &&
      this.props.currentLanguageCode != null &&
      prevProps.currentLanguageCode !== this.props.currentLanguageCode
    if (this.props.isInteractive && languageChanged) {
      this.fetchData()
    }
    const prevAuctionStart = prevProps.auctions[prevProps.match.params.auctionUuid]?.startsAt
    const newAuctionStart = this.props.auctions[this.props.match.params.auctionUuid]?.startsAt
    const isMarketplace = this._isMarketplace()
    if (prevAuctionStart !== newAuctionStart && !!newAuctionStart) {
      this.checkStartTime(newAuctionStart)
    }
    // if we're updating with a change to the user's registration, we want to notify them
    if (this.props.userUuid && this.props.registrantsByUserUuid) {
      const auctionUuid = isMarketplace ? 'marketplace' : this.props.match.params.auctionUuid
      const auctionsToMonitorForRegistrations = isMarketplace ? this.props.partneredAuctions : [auctionUuid]

      auctionsToMonitorForRegistrations.forEach(monitoredAuctionUuid => {
        const registrantApprovalProperty = `registrantsByUserUuid.${monitoredAuctionUuid}.${this.props.userUuid}`
        const previousRegistration = get(prevProps, registrantApprovalProperty, false)
        const currentRegistration = get(this.props, registrantApprovalProperty, false)

        if (previousRegistration &&
          (
            previousRegistration.approved !== currentRegistration.approved ||
            previousRegistration.statusLabel !== currentRegistration.statusLabel
          )
        ) {
          const notificationType = currentRegistration.approved ? 'success' : 'warning'
          const toastContent = decodeEntities(get(this.props, `registrantsByUserUuid.${monitoredAuctionUuid}.${this.props.userUuid}.statusLabel`))
          this.props.addAuctionNotification(auctionUuid, auctionUuid, notificationType, toastContent)
          if (!isMarketplace) {
            const registrationStatusToastId = `registrationToast${monitoredAuctionUuid}`

            const toastType = currentRegistration.approved ? toast.TYPE.SUCCESS : toast.TYPE.WARNING
            const toastAutoClose = toastType === toast.TYPE.SUCCESS && 5000
            if (toast.isActive(this._registrationStatus)) {
              toast.update(this._registrationStatus, {
                autoClose: toastAutoClose,
                toastId: registrationStatusToastId,
                render: toastContent,
                type: toastType
              })
            } else {
              this._registrationStatus = toast(toastContent, {
                autoClose: toastAutoClose,
                toastId: registrationStatusToastId,
                type: toastType
              })
            }
          }
        }
      })
    }
  }

  componentWillUnmount () {
    if (toast.isActive(this._registrationStatus)) {
      toast.dismiss(this._registrationStatus)
    }
    if (this._checkStartTimeLoop) {
      clearTimeout(this._checkStartTimeLoop)
    }
  }

  checkStartTime = (startTime) => {
    const currentTime = new Date().getTime()
    const isPassed = startTime <= currentTime || this._isMarketplace()
    if (isPassed) {
      this.setState({
        hasStarted: true
      })
    } else {
      this._checkStartTimeLoop = setTimeout(this.checkStartTime.bind(this, startTime), 10000)
    }
  }

  // not using arrow function due to iOS parsing issue https://github.com/mobxjs/mobx/issues/1169
  async fetchData () {
    const auctionUuid = this.props.match.params.auctionUuid
    const isMarketplace = this._isMarketplace()
    try {
      this.setState({
        notFound: false
      })
      const auctionData = await this.props.getAuction(isMarketplace ? 'marketplace' : auctionUuid)
      this.props.subscribeToAuction(isMarketplace ? auctionData.partneredAuctions : auctionUuid)
    } catch (err) {
      try {
        const code = JSON.parse(err.message).code
        if (code === '404') {
          this.setState({
            notFound: true
          })
        }
      } catch (e) {
        console.error(e)
      }
      console.error(err)
    }
  }

  render () {
    if (this.state.notFound) return (<ErrorNotFound/>)
    const isMarketplace = this._isMarketplace()
    const auctionUuid = isMarketplace ? 'marketplace' : this.props.match.params.auctionUuid
    const auction = this.props.auctions[auctionUuid]
    const auctionInformation = auction && this.props.informations[auctionUuid]
    const configuration = auction && this.props.configurations[auctionUuid]

    const loading = (
      <div className='page__loader'>
        <Loading/>
      </div>
    )

    if (auction == null || (!isMarketplace && !configuration)) {
      return loading
    }

    /*
     * Note: OR with auction live and hasStarted. This has to be OR and not AND because the auction state will not
     * change unless re-fetched by refreshing the browser. The hasStarted value is periodically tested though, so it
     * becomes true when the auction start is reached
     */
    const ChildComponent = (auction.webcast && auction.status !== 'RECONCILIATION' && auction.status !== 'ARCHIVED' && (auction.live || this.state.hasStarted)) ? WebcastDetails : TimedDetails
    const auctionNotifications = this.props.auctionNotifications[auctionUuid] || []

    const {
      hasCustomTitle,
      hasCustomMetaDescription
    } = this.context
    return (
      <div id='wrapped'>
        <Helmet>
          {hasCustomTitle
            ? <title>{isMarketplace ? '' : auction.title} {translate(isMarketplace ? 'SEOCodes_PAGE_TITLE_MARKETPLACE_PAGE' : 'SEOCodes_SITE_SEO_ON_AUCTION')} | {translate('SEOCodes_SITE_SEO_DEFAULT_TITLE')}</title>
            : null}
          {hasCustomMetaDescription && !isMarketplace
            ? <meta name='description' content={auctionInformation?.description}/>
            : null}
        </Helmet>
        <ChildComponent
          uuid={auctionUuid}
          hasStarted={this.state.hasStarted}
          isInteractive={this.props.isInteractive}
          isMarketplace={isMarketplace}
          sendEvent={this.props.sendEvent}
        />
        {auctionNotifications.map(notification => (
          <ConfirmModal
            key={`notification-${notification.key}`}
            color={notification.type}
            handleClose={() => this.props.handleAuctionNotificationClear(auctionUuid, notification.key)}
            icon={notification.icon || faCheckCircle}
            className={`confirmation-modal--${notification.listingUuid}`}
            id={`confirmation-modal--${notification.key}`}
            isOpen={notification.visible}
            message={notification.message}
          />
        ))}
      </div>
    )
  }
}

AuctionDetails.propTypes = {
  addAuctionNotification: PropTypes.func.isRequired,
  auctionNotifications: PropTypes.object.isRequired,
  auctions: PropTypes.object.isRequired,
  configurations: PropTypes.object,
  getAuction: PropTypes.func.isRequired,
  handleAuctionNotificationClear: PropTypes.func.isRequired,
  informations: PropTypes.object.isRequired,
  isInteractive: PropTypes.bool.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      auctionUuid: PropTypes.string
    }).isRequired,
    path: PropTypes.string.isRequired
  }).isRequired,
  partneredAuctions: PropTypes.arrayOf(PropTypes.string),
  registrantsByUserUuid: PropTypes.object.isRequired,
  sendEvent: PropTypes.func.isRequired,
  subscribeToAuction: PropTypes.func.isRequired,
  userUuid: PropTypes.string,
  currentLanguageCode: PropTypes.string
}

export function mapStateToProps (state) {
  return {
    currentLanguageCode: state.config.currentLanguageCode,
    auctionNotifications: state.notifications.auction,
    auctions: state.auctions.auctions,
    configurations: state.auctions.configurations,
    informations: state.auctions.information,
    partneredAuctions: state.auctions.partneredAuctions,
    registrantsByUserUuid: state.registrations.registrantIdsByUserUuidByAuction,
    userUuid: state.users.user.uuid
  }
}

export function mapDispatchToProps (dispatch) {
  return {
    getAuction: (auctionUuid) => {
      return dispatch(auctions.auctionGet(auctionUuid))
    },
    addAuctionNotification: (auctionUuid, listingUuid, notificationType, message) => {
      return dispatch(notifications.addAuctionNotification(auctionUuid, listingUuid, notificationType, message))
    },
    handleAuctionNotificationClear: (auctionUuid, key) => {
      return dispatch(notifications.clearAuctionNotification(auctionUuid, key))
    }
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(withWebSockets(AuctionDetails)))
